// Session SC2A: the ONE typeahead picker for a lift, a site or a customer. It asks the register for twenty matches as
// the person types (250 ms debounce, two characters to search) instead of loading the whole register to fill a select,
// so a page opens the same way at 1,000 lifts as at 35. The selected record is shown from its id (a label the page
// already knows, else one fetch by id). Combobox keyboard pattern: up, down, enter, escape; the result count is
// announced; rows are 44px tall for a phone.
import { AccountSymbol } from "./AccountSymbol";
import { useEffect, useId, useRef, useState } from "react";
import { apiRequest } from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";

export type RecordPickerKind = "lift" | "site" | "customer";

export interface RecordPickerFilters {
  customerId?: string;
  siteId?: string;
  /** Lifts only: the register class (for example "lift"). */
  class?: string;
  /** Lifts only: false leaves retired items out. */
  includeRetired?: boolean;
  /** Customers only: "all" also offers one-off (non-contract) callers; absent = contract customers only, as the register. */
  customerType?: "all" | "non_contract";
  /** Customers only: also offer archived customers. */
  includeArchived?: boolean;
}

export interface PickerRecord {
  id: string;
  label: string;
  detail: string | null;
  /** The raw row the route returned, for a page that needs another field of the pick. */
  raw: Record<string, unknown>;
}

export const RECORD_PICKER_TAKE = 20;
export const RECORD_PICKER_MIN_CHARS = 2;

const PATH: Record<RecordPickerKind, string> = { lift: "/assets", site: "/sites", customer: "/customers" };
const NOUN: Record<RecordPickerKind, string> = { lift: "lift", site: "site", customer: "customer" };

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

export function toPickerRecord(kind: RecordPickerKind, row: Record<string, unknown>): PickerRecord {
  const id = String(row.id);
  if (kind === "customer") {
    return { id, label: str(row.name) ?? "Customer", detail: str(row.billingEmail), raw: row };
  }
  const customerName = str((row.customer as { name?: unknown } | undefined)?.name);
  if (kind === "site") {
    return { id, label: str(row.name) ?? "Site", detail: [customerName, str(row.postcode)].filter(Boolean).join(" | ") || null, raw: row };
  }
  const site = row.site as { name?: unknown; postcode?: unknown; customer?: { name?: unknown } } | undefined;
  const ele = str(row.eleNumber) ?? str(row.legacyEleNumber) ?? "No ELE number";
  return {
    id,
    label: `${ele} | ${str(site?.name) ?? "Unknown site"}`,
    detail: [str(row.liftType), str(site?.customer?.name)].filter(Boolean).join(" | ") || null,
    raw: row,
  };
}

export function pickerQueryString(kind: RecordPickerKind, q: string, filters: RecordPickerFilters | undefined): string {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  p.set("take", String(RECORD_PICKER_TAKE));
  const f = filters ?? {};
  if (kind === "lift") {
    if (f.siteId) p.set("siteId", f.siteId);
    if (f.customerId) p.set("customerId", f.customerId);
    if (f.class) p.set("class", f.class);
    if (f.includeRetired === false) p.set("includeRetired", "false");
  } else if (kind === "site") {
    if (f.customerId) p.set("customerId", f.customerId);
  } else {
    if (f.customerType) p.set("type", f.customerType);
    if (f.includeArchived) p.set("archived", "all");
  }
  return p.toString();
}

export interface RecordPickerProps {
  kind: RecordPickerKind;
  /** The chosen record's id, or "" / null for none. */
  value: string | null | undefined;
  onChange: (id: string, record: PickerRecord | null) => void;
  filters?: RecordPickerFilters;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  /** An accessible name when the page labels the field itself (no visible label is drawn). */
  ariaLabel?: string;
  required?: boolean;
  error?: string | null;
  /** A label the page already knows for `value`, so the picker need not fetch it. */
  selectedLabel?: string | null;
  /** Called with the chosen record's label once it is known (picked, passed in or fetched by id), so a page can show it elsewhere. */
  onLabel?: (label: string) => void;
  id?: string;
  /** Text the search box opens with (for example the site a report names); the list searches it when the box is focused. */
  initialQuery?: string;
}

export function RecordPicker({ kind, value, onChange, filters, disabled, placeholder, label, ariaLabel, required, error, selectedLabel, onLabel, id, initialQuery }: RecordPickerProps) {
  const autoId = useId();
  const inputId = id ?? `rp-${autoId}`;
  const listId = `${inputId}-list`;
  const [text, setText] = useState(initialQuery ?? "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<PickerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  const [fetchedLabel, setFetchedLabel] = useState<{ id: string; label: string } | null>(null);
  const [pickedLabel, setPickedLabel] = useState<{ id: string; label: string } | null>(null);
  const seq = useRef(0);
  const debounced = useDebouncedValue(text.trim(), 250);
  const filterKey = JSON.stringify(filters ?? {});

  // The selected record's label: from the page, else from this pick, else one fetch by id.
  const hasValue = !!value;
  const knownLabel =
    selectedLabel ?? (pickedLabel && pickedLabel.id === value ? pickedLabel.label : null) ?? (fetchedLabel && fetchedLabel.id === value ? fetchedLabel.label : null);
  useEffect(() => {
    if (value && knownLabel && onLabel) onLabel(knownLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, knownLabel]);
  useEffect(() => {
    if (!value || knownLabel) return;
    let alive = true;
    apiRequest<Record<string, unknown>>(`${PATH[kind]}/${encodeURIComponent(value)}`)
      .then((row) => {
        if (alive) setFetchedLabel({ id: value, label: toPickerRecord(kind, row).label });
      })
      .catch(() => {
        if (alive) setFetchedLabel({ id: value, label: `Selected ${NOUN[kind]}` });
      });
    return () => {
      alive = false;
    };
  }, [kind, value, knownLabel]);

  // Search: only while the list is open; two characters to search, an empty box offers the first twenty alphabetically.
  const searchable = debounced.length === 0 || debounced.length >= RECORD_PICKER_MIN_CHARS;
  useEffect(() => {
    if (!open || hasValue || !searchable) return;
    const mine = ++seq.current;
    setLoading(true);
    setFailed(false);
    apiRequest<Record<string, unknown>[]>(`${PATH[kind]}?${pickerQueryString(kind, debounced, filters)}`, { noCache: true })
      .then((rows) => {
        if (mine !== seq.current) return;
        setResults(rows.slice(0, RECORD_PICKER_TAKE).map((r) => toPickerRecord(kind, r)));
        setActive(-1);
      })
      .catch(() => {
        if (mine === seq.current) setFailed(true);
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasValue, debounced, searchable, kind, filterKey]);

  function pick(rec: PickerRecord) {
    setPickedLabel({ id: rec.id, label: rec.label });
    setText("");
    setOpen(false);
    onChange(rec.id, rec);
  }
  function clear() {
    setPickedLabel(null);
    setFetchedLabel(null);
    setText("");
    setResults([]);
    onChange("", null);
  }

  const status = !open
    ? ""
    : !searchable
      ? `Type at least ${RECORD_PICKER_MIN_CHARS} characters to search.`
      : failed
        ? "Could not load matches."
        : loading
          ? "Searching..."
          : results.length === 0
            ? `No ${NOUN[kind]}s found.`
            : `${results.length} ${results.length === 1 ? "match" : "matches"}${results.length >= RECORD_PICKER_TAKE ? ", type more to narrow" : ""}.`;

  return (
    <div className="relative">
      {label && (
        <label className="mb-1 block text-sm" htmlFor={inputId}>
          {label}
          {required && <span className="text-ele-green"> *</span>}
        </label>
      )}
      {hasValue ? (
        <div className="flex min-h-11 items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2">
          <span id={inputId} className="min-w-0 break-words text-sm text-ele-strong">
            {knownLabel ?? "Fetching..."}
          </span>
          {/* Session SC9B: a customer's account symbol beside the chosen name (a flag, never a block). */}
          {kind === "customer" && <AccountSymbol customerId={value} />}
          {!disabled && (
            <button type="button" onClick={clear} className="min-h-11 shrink-0 px-2 text-xs text-ele-strong underline" aria-label={`Clear ${NOUN[kind]}`}>
              Clear
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-label={label ? undefined : ariaLabel}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-activedescendant={active >= 0 ? `${inputId}-opt-${active}` : undefined}
            autoComplete="off"
            disabled={disabled}
            value={text}
            placeholder={placeholder ?? `Search for a ${NOUN[kind]}`}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
                setActive((a) => Math.min(results.length - 1, a + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(0, a - 1));
              } else if (e.key === "Enter") {
                const rec = results[active >= 0 ? active : results.length === 1 ? 0 : -1];
                if (open && rec) {
                  e.preventDefault();
                  pick(rec);
                }
              } else if (e.key === "Escape") {
                if (open) {
                  e.preventDefault();
                  setOpen(false);
                }
              }
            }}
            className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
          />
          {open && (
            <ul
              id={listId}
              role="listbox"
              aria-label={`${NOUN[kind]} matches`}
              data-vscroll
              className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-lg"
            >
              {searchable &&
                !failed &&
                results.map((rec, i) => (
                  <li
                    key={rec.id}
                    id={`${inputId}-opt-${i}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(rec);
                    }}
                    className={`flex min-h-11 cursor-pointer flex-col justify-center px-3 py-2 hover:bg-ele-inset ${i === active ? "bg-ele-inset" : ""}`}
                  >
                    <span className="break-words text-sm text-ele-strong">
                      {rec.label}
                      {kind === "customer" && <AccountSymbol decorative customerId={rec.id} />}
                    </span>
                    {rec.detail && <span className="break-words text-xs text-ele-body opacity-70">{rec.detail}</span>}
                  </li>
                ))}
              {status && results.length === 0 && (
                <li role="presentation" className="px-3 py-2 text-xs text-ele-body opacity-70">
                  {status}
                </li>
              )}
            </ul>
          )}
        </>
      )}
      <p className="sr-only" role="status" aria-live="polite">
        {status}
      </p>
      {error && (
        <p role="alert" className="mt-1 text-xs text-ele-strong">
          {error}
        </p>
      )}
    </div>
  );
}
